/**
 * UTM Parameter Persistence
 * 
 * This script ensures UTM parameters persist across navigation
 * by storing them in sessionStorage and appending them to all internal links.
 */

(function() {
  'use strict';

  // UTM parameter keys to track
  const UTM_PARAMS = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_term',
    'utm_content',
    'utm_full_discount',
    'utm_discount',
    'utm_discount_type',
    'utm_discount_amount',
    'utm_discount_apply'
  ];

  const STORAGE_KEY = 'storyville_utm_params';

  /**
   * Get all current UTM parameters from the URL
   */
  function getUtmParams() {
    const params = new URLSearchParams(window.location.search);
    const utmParams = {};
    
    UTM_PARAMS.forEach(key => {
      const value = params.get(key);
      if (value) {
        utmParams[key] = value;
      }
    });

    return utmParams;
  }

  /**
   * Store UTM parameters in sessionStorage
   */
  function storeUtmParams(params) {
    if (Object.keys(params).length > 0) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(params));
    }
  }

  /**
   * Retrieve stored UTM parameters from sessionStorage
   */
  function getStoredUtmParams() {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (e) {
      console.error('Error reading stored UTM params:', e);
      return {};
    }
  }

  /**
   * Append UTM parameters to a URL
   */
  function appendUtmToUrl(url, utmParams) {
    if (Object.keys(utmParams).length === 0) {
      return url;
    }

    try {
      const urlObj = new URL(url, window.location.origin);
      Object.keys(utmParams).forEach(key => {
        urlObj.searchParams.set(key, utmParams[key]);
      });
      return urlObj.toString();
    } catch (e) {
      console.error('Error appending UTM params to URL:', e);
      return url;
    }
  }

  /**
   * Check if URL is internal to the site
   */
  function isInternalUrl(href) {
    if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) {
      return false;
    }

    try {
      const url = new URL(href, window.location.origin);
      return url.origin === window.location.origin;
    } catch (e) {
      // If URL parsing fails, check if it starts with / or is relative
      return href.startsWith('/') || !href.includes('://');
    }
  }

  /**
   * Handle link click to append UTM parameters
   */
  function handleLinkClick(event) {
    const link = event.currentTarget;
    const href = link.getAttribute('href');

    if (!href || !isInternalUrl(href)) {
      return;
    }

    // Skip if clicking on already modified links
    if (link.dataset.utmModified === 'true') {
      return;
    }

    // Skip checkout and external URLs
    if (href.includes('/checkout') || href.includes('/apps/')) {
      // Handle checkout URL specifically for discount code
      const storedParams = getStoredUtmParams();
      if (storedParams.utm_discount && !href.includes('discount=')) {
        const separator = href.includes('?') ? '&' : '?';
        link.href = href + separator + 'discount=' + encodeURIComponent(storedParams.utm_discount);
      }
      return;
    }

    const storedParams = getStoredUtmParams();
    if (Object.keys(storedParams).length === 0) {
      return;
    }

    const modifiedUrl = appendUtmToUrl(href, storedParams);
    if (modifiedUrl !== href) {
      link.href = modifiedUrl;
      link.dataset.utmModified = 'true';
    }
  }

  /**
   * Set up event delegation for all links
   */
  function setupLinkInterception() {
    // Use event delegation for all link clicks
    document.addEventListener('click', function(event) {
      const link = event.target.closest('a');
      if (link && link.hasAttribute('href')) {
        handleLinkClick(event);
      }
    }, true);
  }

  /**
   * Update existing links with UTM parameters
   */
  function updateExistingLinks() {
    const links = document.querySelectorAll('a[href]');
    const storedParams = getStoredUtmParams();
    
    if (Object.keys(storedParams).length === 0) {
      return;
    }

    links.forEach(link => {
      const href = link.getAttribute('href');
      if (href && isInternalUrl(href) && !link.dataset.utmModified) {
        // Skip if URL already has the params we're adding
        const urlHasUtmParams = UTM_PARAMS.some(key => {
          return href.includes(key + '=');
        });
        
        if (!urlHasUtmParams) {
          const modifiedUrl = appendUtmToUrl(href, storedParams);
          if (modifiedUrl !== href) {
            link.href = modifiedUrl;
            link.dataset.utmModified = 'true';
          }
        }
      }
    });
  }

  /**
   * Handle programmatic navigation (fetch requests, AJAX)
   */
  function setupHistoryListener() {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function() {
      originalPushState.apply(history, arguments);
      updateExistingLinks();
    };

    history.replaceState = function() {
      originalReplaceState.apply(history, arguments);
    };

    // Handle instant-page.js prefetching
    if (window.instant) {
      // Override instant's prefetch to add UTM params
      const originalPrefetch = window.instant.prefetch;
      if (originalPrefetch) {
        window.instant.prefetch = function(url) {
          const storedParams = getStoredUtmParams();
          if (Object.keys(storedParams).length > 0 && isInternalUrl(url)) {
            const modifiedUrl = appendUtmToUrl(url, storedParams);
            originalPrefetch.call(window.instant, modifiedUrl);
          } else {
            originalPrefetch.call(window.instant, url);
          }
        };
      }
    }
  }

  /**
   * Initialize on page load
   */
  function init() {
    // Check for new UTM parameters in URL
    const currentUtmParams = getUtmParams();
    const storedParams = getStoredUtmParams();

    // If there are new UTM params, store them
    if (Object.keys(currentUtmParams).length > 0) {
      // Merge with existing stored params (URL params take precedence)
      const mergedParams = { ...storedParams, ...currentUtmParams };
      storeUtmParams(mergedParams);
    }

    // Set up link interception
    setupLinkInterception();
    setupHistoryListener();

    // Update existing links on the page
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', updateExistingLinks);
    } else {
      updateExistingLinks();
    }

    // Watch for dynamically added content
    if (window.MutationObserver) {
      const observer = new MutationObserver(function(mutations) {
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length) {
            updateExistingLinks();
          }
        });
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize when script loads
  init();

  // Export functions for use in checkout
  window.StoryvilleUtm = {
    getStoredUtmParams: getStoredUtmParams,
    getUtmDiscount: function() {
      const params = getStoredUtmParams();
      return params.utm_discount || null;
    }
  };

})();
